import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, AlertCircle, XCircle, Package } from "lucide-react";
import { cn } from "@/lib/utils";

interface StatusSummaryBarProps {
  stats: {
    total: number;
    ready: number;
    needsFix: number;
    blocked: number;
  };
  className?: string;
  sticky?: boolean;
}

export const StatusSummaryBar = ({ 
  stats, 
  className,
  sticky = false 
}: StatusSummaryBarProps) => {
  const statusItems = [
    {
      label: 'Total',
      count: stats.total,
      icon: Package,
      color: 'text-foreground',
      bgColor: 'bg-muted/50',
      borderColor: 'border-border'
    },
    {
      label: 'Ready',
      count: stats.ready,
      icon: CheckCircle,
      color: 'text-success',
      bgColor: 'bg-success/10',
      borderColor: 'border-success/20'
    },
    {
      label: 'Needs Fix',
      count: stats.needsFix,
      icon: AlertCircle,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/20'
    },
    {
      label: 'Blocked',
      count: stats.blocked,
      icon: XCircle,
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      borderColor: 'border-destructive/20'
    }
  ];

  return (
    <Card className={cn(
      "border-muted shadow-sm",
      sticky && "sticky top-4 z-10",
      className
    )}>
      <CardContent className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {statusItems.map((item) => {
            const Icon = item.icon;
            
            return (
              <div key={item.label} className="flex items-center gap-3">
                <div className={cn(
                  "p-2 rounded-lg transition-colors",
                  item.bgColor
                )}>
                  <Icon className={cn("w-4 h-4", item.color)} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
                    {item.label}
                  </p>
                  <p className={cn("text-lg font-bold tabular-nums", item.color)}>
                    {item.count}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Progress indicator */}
        <div className="mt-4 pt-3 border-t border-border">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>Completion Progress</span>
            <span className="font-medium">
              {stats.total > 0 ? Math.round((stats.ready / stats.total) * 100) : 0}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-success h-2 rounded-full transition-all duration-300 ease-out"
              style={{ 
                width: stats.total > 0 ? `${(stats.ready / stats.total) * 100}%` : '0%' 
              }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};